import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  ClipboardDocumentIcon, 
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { getCodeExamples } from '../../utils/apiCodeExamples'
import { trackCodeLanguageSwitch, trackCodeExampleCopied } from '../../config/analyticsEvents'

const APICodeExamples = ({ copyToClipboard, userInfo }) => {
  const [activeLanguage, setActiveLanguage] = useState('curl')

  const codeExamples = getCodeExamples(userInfo)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
      <div className="border-b border-gray-200">
        <div className="flex space-x-8 px-6">
          {Object.keys(codeExamples).map((lang) => (
            <button
              key={lang}
              onClick={() => { setActiveLanguage(lang); trackCodeLanguageSwitch(lang) }}
              className={`py-4 text-sm font-medium capitalize border-b-2 transition-colors ${
                activeLanguage === lang
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {{ javascript: 'JavaScript', csharp: 'C#' }[lang] || lang}
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Code Example</h3>
          <p className="text-sm text-gray-600">
            Copy the {activeLanguage} example below:
          </p>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 relative">
          <pre className="text-green-400 text-sm overflow-x-auto">
            <code>{codeExamples[activeLanguage]}</code>
          </pre>
          <button
            onClick={() => { copyToClipboard(codeExamples[activeLanguage], `${{ javascript: 'JavaScript', csharp: 'C#' }[activeLanguage] || activeLanguage} example`); trackCodeExampleCopied(activeLanguage) }}
            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors"
            title="Copy to clipboard"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}


// Postman-style HTTP Method Badge
const HttpMethodBadge = ({ method }) => {
  const colors = {
    'GET': 'bg-green-100 text-green-800 border-green-200',
    'POST': 'bg-orange-100 text-orange-800 border-orange-200',
    'PUT': 'bg-blue-100 text-blue-800 border-blue-200',
    'DELETE': 'bg-red-100 text-red-800 border-red-200'
  }
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors[method] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
      {method}
    </span>
  )
}

// Postman-style Endpoint Card
const EndpointCard = ({ method, endpoint, title, description, children }) => {
  return (
    <div className="bg-white">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3 mb-3">
          <HttpMethodBadge method={method} />
          <code className="text-lg font-mono bg-gray-100 px-3 py-1 rounded text-gray-800 flex-1">
            {endpoint}
          </code>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  )
}

// Postman-style Tab Navigation
const PostmanTabs = ({ activeTab, setActiveTab, tabs }) => {
  return (
    <div className="border-b border-gray-200 mb-6">
      <nav className="flex space-x-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </nav>
    </div>
  )
}

// Postman-style Parameter Table
const ParameterTable = ({ parameters }) => {
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Parameter</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Required</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Description</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {parameters.map((param, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono text-blue-600">{param.name}</code>
                  {param.proOnly && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-gradient-to-r from-blue-500 to-emerald-500 text-white">
                      Pro
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {param.type}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {param.required ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
              <td className="px-6 py-4 text-sm text-gray-900 min-w-[300px]">
                <div>{param.description}</div>
                {param.example && (
                  <div className="mt-1">
                    <span className="text-xs text-gray-500">Example: </span>
                    <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{param.example}</code>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}


const GenerateIconsSection = ({ copyToClipboard, userInfo }) => {
  const [activeTab, setActiveTab] = useState('params')
  
  const tabs = [
    { id: 'params', name: 'Parameters' },
    { id: 'headers', name: 'Headers' },
    { id: 'request', name: 'Request' },
    { id: 'response', name: 'Response' }
  ]

  const headers = [
    {
      name: 'Content-Type',
      type: 'string',
      required: true,
      description: 'Must be set to application/json',
      example: 'application/json'
    },
    {
      name: 'Authorization',
      type: 'string',
      required: true,
      description: 'Your API key for authentication',
      example: 'YOUR_API_KEY'
    }
  ]

  const parameters = [
    {
      name: 'uploadedImage',
      type: 'string',
      required: true,
      description: 'Base64 encoded image with data URL prefix (max 5MB)',
      example: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...'
    },
    {
      name: 'userId',
      type: 'string',
      required: true,
      description: 'User ID for quota tracking and authentication',
      example: userInfo?.id || 'YOUR_USER_ID'
    },
    {
      name: 'backgroundColor',
      type: 'string',
      required: false,
      description: 'Background color in hex format (default: #ffffff)',
      example: '#ffffff'
    },
    {
      name: 'iconName',
      type: 'string',
      required: false,
      description: 'Name for the icon files (default: ic_launcher)',
      example: 'ic_launcher'
    },
    {
      name: 'padding',
      type: 'integer',
      required: false,
      description: 'Padding/zoom percentage: positive values (0-100) add padding, negative values (-1 to -100) zoom/scale up (default: 0)',
      example: '10'
    },
    {
      name: 'format',
      type: 'string',
      required: false,
      description: 'Android image format: "png" or "webp". iOS icons will always use PNG (default: png)',
      example: 'png'
    },
    {
      name: 'showBadge',
      type: 'boolean',
      required: false,
      description: 'Whether to show badge overlay (default: false)',
      example: 'false',
      proOnly: true
    },
    {
      name: 'badgeText',
      type: 'string',
      required: false,
      description: 'Text to display on badge (default: "")',
      example: 'BETA',
      proOnly: true
    },
    {
      name: 'badgeColor',
      type: 'string',
      required: false,
      description: 'Badge background color in hex (default: #333333)',
      example: '#333333',
      proOnly: true
    },
    {
      name: 'downloadUrl',
      type: 'boolean',
      required: false,
      description: 'If true, returns S3 pre-signed URL instead of direct ZIP (default: false)',
      example: 'false',
      proOnly: true
    }
  ]

  const requestExample = JSON.stringify({
    uploadedImage: "data:image/png;base64,YOUR_BASE64_IMAGE",
    userId: userInfo?.id || "{USER_ID_HERE}",
    backgroundColor: "#000000",
    iconName: "ic_launcher",
    padding: 0,
    showBadge: false,
    badgeText: "",
    badgeColor: "#000000",
    format: "png"
  }, null, 2)

  const directZipResponse = `HTTP/1.1 200 OK
Content-Type: application/zip
Content-Disposition: attachment; filename="easyappicon-icons-1234567890123.zip"
Access-Control-Allow-Origin: *
X-Quota-Used: 5
X-Quota-Remaining: 95
X-Quota-Reset: 2024-02-01T00:00:00Z

[Binary ZIP file content]`

  const s3UrlResponse = JSON.stringify({
    downloadUrl: "https://st.easyappicon.com/icons.zip?x-id=GetObject&X-Amz-Algorithm=...",
    filename: "icons.zip",
    requestId: "550e8400-e29b-41d4-a716-446655440000"
  }, null, 2)

  const quotaExceededResponse = JSON.stringify({
    error: "QuotaExceededError",
    message: "Quota exceeded: Monthly limit of 100 credits reached. Resets on 2024-02-01T00:00:00Z."
  }, null, 2)

  const badRequestResponse = JSON.stringify({
    error: "InvalidRequestError",
    message: "Invalid request: uploadedImage is required"
  }, null, 2)

  const payloadTooLargeResponse = JSON.stringify({
    error: "InvalidRequestError",
    message: "Invalid request: Image too large. Maximum size is 5MB (base64 encoded)"
  }, null, 2)

  const invalidFormatResponse = JSON.stringify({
    error: "InvalidRequestError",
    message: "Invalid request: Format must be 'png' or 'webp'"
  }, null, 2)

  const unauthorizedResponse = JSON.stringify({
    error: "AuthenticationError",
    message: "Authentication failed: Missing Authorization header"
  }, null, 2)

  const statusCodes = [
    { code: '200', title: 'OK', description: 'Icons generated successfully', color: 'text-green-600' },
    { code: '400', title: 'Bad Request', description: 'Invalid input parameters', color: 'text-red-600' },
    { code: '401', title: 'Unauthorized', description: 'Invalid or missing API key', color: 'text-red-600' },
    { code: '404', title: 'Not Found', description: 'User not found', color: 'text-red-600' },
    { code: '429', title: 'Too Many Requests', description: 'Quota exceeded', color: 'text-orange-600' },
    { code: '500', title: 'Internal Server Error', description: 'Unexpected server error', color: 'text-red-600' }
  ]

  return (
    <EndpointCard
      method="POST"
      endpoint="/generate"
      title="Generate App Icons"
      description="Generate iOS and Android app icons from an uploaded image. Returns a ZIP file containing all icon sizes."
    >
      {/* Code Examples - Displayed at Top */}
      <div className="mb-8 pb-6 border-b border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Code Examples</h4>
        <APICodeExamples copyToClipboard={copyToClipboard} userInfo={userInfo} />
      </div>

      <PostmanTabs activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs} />
      
      {activeTab === 'headers' && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Request Headers</h4>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-sm font-semibold">!</span>
                </div>
              </div>
              <div>
                <h5 className="font-medium text-blue-900">Authentication Required</h5>
                <p className="text-sm text-blue-700 mt-1">
                  This endpoint requires API key authentication. You must include your API key in the Authorization header.
                </p>
              </div>
            </div>
          </div>
          <ParameterTable parameters={headers} />
        </div>
      )}

      {activeTab === 'params' && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Request Parameters</h4>
          <ParameterTable parameters={parameters} />
        </div>
      )}

      {activeTab === 'request' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Request Body</h4>
            <button
              onClick={() => copyToClipboard(requestExample, 'Request body')}
              className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-800 border border-orange-200 rounded hover:bg-orange-50"
            >
              <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
              Copy
            </button>
          </div>
          <div className="bg-gray-900 rounded-lg p-4">
            <pre className="text-green-400 text-sm overflow-x-auto">
              <code>{requestExample}</code>
            </pre>
          </div>
      </div>
      )}
      
      {activeTab === 'response' && (
        <div className="space-y-6">
          {/* Status Codes */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">HTTP Status Codes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {statusCodes.map((status, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <code className={`font-mono text-sm font-bold ${status.color}`}>
                    {status.code}
                  </code>
          <div>
                    <div className="font-medium text-gray-900 text-sm">{status.title}</div>
                    <div className="text-xs text-gray-600">{status.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Success Responses */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Success Responses (200)</h4>
            
            {/* Direct ZIP Response */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-medium text-gray-900">Direct ZIP Response (downloadUrl: false)</h5>
                <button
                  onClick={() => copyToClipboard(directZipResponse, 'Direct ZIP response')}
                  className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-800 border border-orange-200 rounded hover:bg-orange-50"
                >
                  <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                  Copy
                </button>
              </div>
              <div className="bg-gray-900 rounded-lg p-4">
                <pre className="text-green-400 text-sm overflow-x-auto">
                  <code>{directZipResponse}</code>
                </pre>
              </div>
            </div>

            {/* S3 URL Response */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-medium text-gray-900">Download URL Response (downloadUrl: true)</h5>
                <button
                  onClick={() => copyToClipboard(s3UrlResponse, 'S3 URL response')}
                  className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-800 border border-orange-200 rounded hover:bg-orange-50"
                >
                  <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                  Copy
                </button>
              </div>
              <div className="bg-gray-900 rounded-lg p-4">
                <pre className="text-green-400 text-sm overflow-x-auto">
                  <code>{s3UrlResponse}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Error Responses */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Error Responses</h4>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-md font-medium text-gray-900">Quota Exceeded (429)</h5>
                  <button
                    onClick={() => copyToClipboard(quotaExceededResponse, 'Quota exceeded response')}
                    className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-800 border border-orange-200 rounded hover:bg-orange-50"
                  >
                    <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                    Copy
                  </button>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <pre className="text-red-400 text-sm overflow-x-auto">
                    <code>{quotaExceededResponse}</code>
                  </pre>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-md font-medium text-gray-900">Unauthorized (401)</h5>
                  <button
                    onClick={() => copyToClipboard(unauthorizedResponse, 'Unauthorized response')}
                    className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-800 border border-orange-200 rounded hover:bg-orange-50"
                  >
                    <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                    Copy
                  </button>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <pre className="text-red-400 text-sm overflow-x-auto">
                    <code>{unauthorizedResponse}</code>
                  </pre>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-md font-medium text-gray-900">Bad Request (400)</h5>
                  <button
                    onClick={() => copyToClipboard(badRequestResponse, 'Bad request response')}
                    className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-800 border border-orange-200 rounded hover:bg-orange-50"
                  >
                    <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                    Copy
                  </button>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <pre className="text-red-400 text-sm overflow-x-auto">
                    <code>{badRequestResponse}</code>
                  </pre>
                </div>
              </div>
      
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-md font-medium text-gray-900">Payload Too Large (413)</h5>
                  <button
                    onClick={() => copyToClipboard(payloadTooLargeResponse, 'Payload too large response')}
                    className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-800 border border-orange-200 rounded hover:bg-orange-50"
                  >
                    <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                    Copy
                  </button>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <pre className="text-red-400 text-sm overflow-x-auto">
                    <code>{payloadTooLargeResponse}</code>
                  </pre>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-md font-medium text-gray-900">Invalid Format (400)</h5>
                  <button
                    onClick={() => copyToClipboard(invalidFormatResponse, 'Invalid format response')}
                    className="flex items-center px-2 py-1 text-xs text-orange-600 hover:text-orange-800 border border-orange-200 rounded hover:bg-orange-50"
                  >
                    <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                    Copy
                  </button>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <pre className="text-red-400 text-sm overflow-x-auto">
                    <code>{invalidFormatResponse}</code>
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </EndpointCard>
  )
}

const ApiDocsTab = ({ copyToClipboard, userInfo }) => {

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Quick Start Guide</h2>
        <p className="text-gray-600">API documentation and integration examples to get you started</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="h-full min-h-[800px] bg-white rounded-lg border border-gray-200 overflow-hidden"
      >
        <div className="overflow-y-auto">
            <GenerateIconsSection copyToClipboard={copyToClipboard} userInfo={userInfo} />
        </div>
      </motion.div>
    </div>
  )
}

export default ApiDocsTab
